import React from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { AlertCircle, ChevronDown } from 'lucide-react';

function DetailsToggle({ requestId, children }) {
  const [open, setOpen] = React.useState(false);
  if (!requestId && !children) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm">
          <ChevronDown
            className={`h-4 w-4 mr-1 transition-transform ${open ? 'rotate-180' : ''}`}
          />
          {open ? 'Hide technical details' : 'Show technical details'}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 px-2">
          {requestId && (
            <span className="text-xs text-muted-foreground block">
              Request ID: {requestId}
            </span>
          )}
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function InlineError({
  title = 'Something went wrong',
  message,
  actionLabel = 'Retry',
  onAction,
  requestId,
}) {
  return (
    <div
      role="alert"
      className="flex flex-row items-center gap-2 px-4 py-2 rounded border border-destructive/50 bg-destructive/10"
    >
      <AlertCircle className="h-5 w-5 text-destructive" aria-hidden />
      <div className="flex-grow">
        <p className="text-sm font-semibold text-destructive">{title}</p>
        {message && <p className="text-sm text-muted-foreground">{message}</p>}
        <DetailsToggle requestId={requestId} />
      </div>
      {onAction && (
        <Button
          variant="outline"
          size="sm"
          onClick={onAction}
          className="text-destructive border-destructive"
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

export function PageError({
  title = 'We hit a snag',
  message = 'Please try again or contact support if the problem continues.',
  requestId,
  primaryActionLabel = 'Reload app',
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  children,
}) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-6 items-center justify-center py-16 px-4"
    >
      <AlertCircle className="h-16 w-16 text-destructive" aria-hidden />
      <div className="flex flex-col gap-2 items-center">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-base text-muted-foreground text-center max-w-[480px]">
          {message}
        </p>
        <DetailsToggle requestId={requestId}>{children}</DetailsToggle>
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        {onPrimaryAction && (
          <Button onClick={onPrimaryAction}>{primaryActionLabel}</Button>
        )}
        {secondaryActionLabel && onSecondaryAction && (
          <Button variant="outline" onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export function RetryPanel({ message, onRetry, requestId }) {
  return (
    <div className="flex flex-col gap-4 items-center py-8">
      <p className="text-base text-muted-foreground" role="status">
        {message}
      </p>
      <Button onClick={onRetry}>Try again</Button>
      <DetailsToggle requestId={requestId} />
    </div>
  );
}
